import type { SignalPayload } from './events';

/**
 * Calls are relay-only: media always goes through the TURN relay, so participants never learn each other's
 * IP addresses. Only relay candidates cross the signalling channel, with their related address (the
 * client's own address the relay was allocated from) blanked, and SDP carries no connection addresses.
 */

/** The ICE candidate type ("host", "srflx", "prflx", "relay") of a candidate line, or null. */
export function candidateType(line: string): string | null {
  const m = /\styp\s+(\S+)/.exec(line);
  return m ? m[1]!.toLowerCase() : null;
}

/** A relay candidate with its related address blanked, or null for any other kind of candidate. */
export function sanitizeCandidate(line: string): string | null {
  if (candidateType(line) !== 'relay') return null;
  return line.replace(/\sraddr\s+\S+/, ' raddr 0.0.0.0').replace(/\srport\s+\d+/, ' rport 0');
}

/** Session description with only relay candidates and no connection addresses. */
export function sanitizeSdp(sdp: string): string {
  const out: string[] = [];
  for (const line of sdp.split('\r\n')) {
    if (line.startsWith('a=candidate:')) {
      const clean = sanitizeCandidate(line);
      if (clean) out.push(clean);
    } else if (line.startsWith('c=IN IP4 ')) {
      out.push('c=IN IP4 0.0.0.0');
    } else if (line.startsWith('c=IN IP6 ')) {
      out.push('c=IN IP6 ::');
    } else if (line.startsWith('a=rtcp:')) {
      out.push(line.replace(/ IN IP4 \S+/, ' IN IP4 0.0.0.0').replace(/ IN IP6 \S+/, ' IN IP6 ::'));
    } else {
      out.push(line);
    }
  }
  return out.join('\r\n');
}

const STREAM_KINDS = new Set(['camera', 'screen', 'audio']);

/**
 * A signalling message rebuilt from only the fields peers use, with every address except the relay's
 * removed. Null means drop it: malformed, or a candidate that is not a relay candidate.
 */
export function sanitizeSignal(signal: unknown): SignalPayload | null {
  if (!signal || typeof signal !== 'object') return null;
  const s = signal as Record<string, unknown>;
  if (s.kind === 'description') {
    const d = s.description as Record<string, unknown> | null | undefined;
    const type = d?.type;
    if (type !== 'offer' && type !== 'answer' && type !== 'pranswer' && type !== 'rollback') return null;
    if (d!.sdp === undefined) return { kind: 'description', description: { type } };
    if (typeof d!.sdp !== 'string') return null;
    return { kind: 'description', description: { type, sdp: sanitizeSdp(d!.sdp) } };
  }
  if (s.kind === 'candidate') {
    if (s.candidate === null) return { kind: 'candidate', candidate: null };
    const c = s.candidate as Record<string, unknown> | undefined;
    if (!c || typeof c !== 'object') return null;
    const text = typeof c.candidate === 'string' ? c.candidate : '';
    // An empty candidate marks the end of a generation's candidates.
    const clean = text === '' ? '' : sanitizeCandidate(text);
    if (clean === null) return null;
    return {
      kind: 'candidate',
      candidate: {
        candidate: clean,
        sdpMid: typeof c.sdpMid === 'string' ? c.sdpMid : null,
        sdpMLineIndex: typeof c.sdpMLineIndex === 'number' ? c.sdpMLineIndex : null,
        usernameFragment: typeof c.usernameFragment === 'string' ? c.usernameFragment : null,
      },
    };
  }
  if (s.kind === 'meta') {
    if (!s.streams || typeof s.streams !== 'object') return null;
    const streams: Record<string, 'camera' | 'screen' | 'audio'> = {};
    for (const [id, kind] of Object.entries(s.streams as Record<string, unknown>).slice(0, 16)) {
      if (typeof kind === 'string' && STREAM_KINDS.has(kind)) streams[id] = kind as 'camera' | 'screen' | 'audio';
    }
    return { kind: 'meta', streams };
  }
  return null;
}

/** TURN entries only (STUN is unused when media must be relayed), or none when no relay is configured. */
export function relayServers<T extends { urls: string | string[]; username?: string; credential?: string }>(servers: T[]): T[] {
  const out: T[] = [];
  for (const server of servers) {
    const urls = (Array.isArray(server.urls) ? server.urls : [server.urls]).filter((u) => /^turns?:/i.test(u));
    if (urls.length > 0 && server.username && server.credential) out.push({ ...server, urls });
  }
  return out;
}
